import { athenaFetch as betterFetch } from "../fetch.ts";
import type { OAuthProvider, ProviderOptions } from "../oauth2/index.ts";
import {
  generateCodeChallenge,
  refreshAccessToken,
  validateAuthorizationCode,
} from "../oauth2/index.ts";
export interface DiscordProfile extends Record<string, unknown> {
  /** the user's banner color encoded as an integer representation of hexadecimal color code */
  accent_color: number | null;
  /**
   * the user's avatar hash:
   * https://discord.com/developers/docs/reference#image-formatting
   */
  avatar: string | null;
  /**
   * undocumented field; corresponds to the Discord feature where you can e.g.
   * put your avatar inside of an ice cube
   */
  avatar_decoration: string | null;
  /**
   * the user's banner hash:
   * https://discord.com/developers/docs/reference#image-formatting
   */
  banner: string | null;
  /**
   * undocumented field; corresponds to the premium feature where you can
   * select a custom banner color
   */
  banner_color: string | null;
  /** whether the user belongs to an OAuth2 application */
  bot?: boolean | undefined;
  /** the user's Discord-tag */
  discriminator: string;
  /** undocumented field; corresponds to the user's custom nickname */
  display_name: string | null;
  /** the user's email */
  email?: string | null;
  /**
   * the flags on a user's account:
   * https://discord.com/developers/docs/resources/user#user-object-user-flags
   */
  flags: number;
  /** the user's display name, if it is set  */
  global_name: string | null;
  /** the user's id (i.e. the numerical snowflake) */
  id: string;
  /** undocumented field; the CDN URL of their profile picture */
  image_url: string;

  /**
   * the user's chosen language option:
   * https://discord.com/developers/docs/reference#locales
   */
  locale: string;
  /** whether the user has two factor enabled on their account */
  mfa_enabled: boolean;
  /**
   * the type of Nitro subscription on a user's account:
   * https://discord.com/developers/docs/resources/user#user-object-premium-types
   */
  premium_type: number;
  /**
   * the public flags on a user's account:
   * https://discord.com/developers/docs/resources/user#user-object-user-flags
   */
  public_flags: number;
  /**
   * whether the user is an Official Discord System user (part of the urgent
   * message system)
   */
  system?: boolean | undefined;
  /** the user's username, not unique across the platform */
  username: string;
  /** whether the email on this account has been verified */
  verified: boolean;
}

export interface DiscordOptions extends ProviderOptions<DiscordProfile> {
  clientId: string;
  permissions?: number | undefined;
  prompt?: ("none" | "consent") | undefined;
}

export const discord = (options: DiscordOptions) => {
  const tokenEndpoint = "https://discord.com/api/oauth2/token";
  return {
    async createAuthorizationURL({ state, scopes, redirectURI, codeVerifier }) {
      const _scopes = options.disableDefaultScope ? [] : ["identify", "email"];
      if (scopes) {
        _scopes.push(...scopes);
      }
      if (options.scope) {
        _scopes.push(...options.scope);
      }
      const hasBotScope = _scopes.includes("bot");
      const permissionsParam =
        hasBotScope && options.permissions !== undefined
          ? `&permissions=${options.permissions}`
          : "";
      const url = new URL(
        `https://discord.com/api/oauth2/authorize?scope=${_scopes.join(
          "+"
        )}&response_type=code&client_id=${
          options.clientId
        }&redirect_uri=${encodeURIComponent(
          options.redirectURI || redirectURI
        )}&state=${state}&prompt=${options.prompt || "none"}${permissionsParam}`
      );
      if (codeVerifier) {
        url.searchParams.set("code_challenge_method", "S256");
        url.searchParams.set(
          "code_challenge",
          await generateCodeChallenge(codeVerifier),
        );
      }
      return url;
    },
    async getUserInfo(token) {
      if (options.getUserInfo) {
        return options.getUserInfo(token);
      }
      const { data: profile, error } = await betterFetch<DiscordProfile>(
        "https://discord.com/api/users/@me",
        {
          headers: {
            authorization: `Bearer ${token.accessToken}`,
          },
        }
      );

      if (error) {
        return null;
      }
      if (profile.avatar === null) {
        const defaultAvatarNumber =
          profile.discriminator === "0"
            ? Number(BigInt(profile.id) >> BigInt(22)) % 6
            : Number.parseInt(profile.discriminator, 10) % 5;
        profile.image_url = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarNumber}.png`;
      } else {
        const format = profile.avatar.startsWith("a_") ? "gif" : "png";
        profile.image_url = `https://cdn.discordapp.com/avatars/${profile.id}/${profile.avatar}.${format}`;
      }
      const userMap = await options.mapProfileToUser?.(profile);
      return {
        data: profile,
        user: {
          email: profile.email,
          emailVerified: profile.verified,
          id: profile.id,
          image: profile.image_url,
          name: profile.global_name || profile.username || "",
          ...userMap,
        },
      };
    },
    id: "discord",
    name: "Discord",
    options,
    refreshAccessToken: options.refreshAccessToken
      ? options.refreshAccessToken
      : async (refreshToken) =>
          refreshAccessToken({
            options: {
              clientId: options.clientId,
              clientKey: options.clientKey,
              clientSecret: options.clientSecret,
            },
            refreshToken,
            tokenEndpoint,
          }),
    validateAuthorizationCode: async ({ code, redirectURI }) =>
      validateAuthorizationCode({
        code,
        options,
        redirectURI,
        tokenEndpoint,
      }),
  } satisfies OAuthProvider<DiscordProfile>;
};
